import { isCurrentSession } from './sessionPolicy';
import userAuth from './userauth';
import { APPCONFIG } from '@/app.config';
import { jwtDecode } from '@Shared/Utils/helpers';
import queryClient from '@Shared/Query/queryClient';

let refreshTokenPromise = null;

export const getLoginPath = () => {
    const internal = userAuth.accountType() === 2;
    return internal ? '/admin' : '/login';
};

const getSessionUserId = () => userAuth.simulatorUserId() || userAuth.userId() || '';

export const signoutToLogin = (loginPath = getLoginPath()) => {
    queryClient.clear();
    userAuth.signout(() => { window.location.href = loginPath; });
};

const isTokenExpiredOrExpiring = (token, minValidityMs = 0) => {
    if (!token) return true;

    try {
        const decode = jwtDecode(token);
        return (decode.exp * 1000) <= (Date.now() + minValidityMs);
    } catch {
        return true;
    }
};

export const USER_TOKEN = {
    set: ({ token, refreshToken }) => {
        let claims;
        try { claims = jwtDecode(token); } catch { claims = {}; }
        if (!isCurrentSession(claims)) { userAuth.signout(() => {}); throw new Error('La sesión no es compatible. Volvé a iniciar sesión.'); }
        userAuth.getStorage().setItem('access_token', token);
        userAuth.getStorage().setItem('refresh_token', refreshToken);
    },
    remove: () => {
        userAuth.getStorage().removeItem('access_token');
        userAuth.getStorage().removeItem('refresh_token');
    },
    get: () => ({
        token: userAuth.getStorage().getItem('access_token'),
        refreshToken: userAuth.getStorage().getItem('refresh_token'),
    }),
    refresh_token: ({ force = false, minValidityMs = 0, redirectOnFailure = true, request } = {}) => {
        const token = USER_TOKEN.get().token;
        const refresh_token = USER_TOKEN.get().refreshToken;

        if (token && !userAuth.hasCurrentSession()) {
            if (redirectOnFailure) signoutToLogin();
            else { queryClient.clear(); userAuth.signout(() => {}); }
            return Promise.resolve(false);
        }
        if (!token || !refresh_token) {
            return Promise.resolve(false);
        }

        if (!force && !isTokenExpiredOrExpiring(token, minValidityMs)) {
            return Promise.resolve(true);
        }

        if (refreshTokenPromise) {
            return refreshTokenPromise;
        }

        if (!request) {
            return Promise.resolve(false);
        }

        refreshTokenPromise = request(APPCONFIG.ENDPOINTS.PRODUCERURL + 'api/login/refreshtoken', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                "token": token,
                "refreshToken": refresh_token,
                "userIdSession": getSessionUserId()
            })
        })
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
                if (USER_TOKEN.get().token !== token || USER_TOKEN.get().refreshToken !== refresh_token) return false;
                if (data?.success && data.token && data.refreshToken) {
                    USER_TOKEN.set({ token: data.token, refreshToken: data.refreshToken });
                    return true;
                }

                if (redirectOnFailure) signoutToLogin();
                else { queryClient.clear(); userAuth.signout(() => {}); }
                return false;
            })
            .catch(() => {
                if (USER_TOKEN.get().token !== token || USER_TOKEN.get().refreshToken !== refresh_token) return false;
                if (redirectOnFailure) signoutToLogin();
                else { queryClient.clear(); userAuth.signout(() => {}); }
                return false;
            })
            .finally(() => {
                refreshTokenPromise = null;
            });

        return refreshTokenPromise;
    },
    get notEmpty() {
        return userAuth.hasCurrentSession();
    },
};

export const DEFAULT_HEADER = {
    get: () => {
        const { token, refreshToken } = USER_TOKEN.get();
        const headers = {
            'Content-Type': 'application/json;charset=UTF-8',
            'Application': 'Life',
            'UserId': userAuth.userId() ?? '',
        };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (refreshToken) headers['RefreshToken'] = refreshToken;
        return headers;
    },
};
